import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BackToBackLcService } from '../../services/backToBackLc.service';
import { B2B_STATUS_COLORS, B2bLcStatus, BackToBackLc } from '../../interfaces/commercial.interface';
import { formatMoney } from '../../utils/format';
import { enumLabel } from '../../utils/garmentsLabels';

const B2B_STATUSES: B2bLcStatus[] = ['OPENED', 'PARTIALLY_UTILIZED', 'FULLY_UTILIZED', 'CLOSED'];

/**
 * §7 (re-review N15): institution-wide register of every back-to-back LC, reachable as its own
 * catalog link — distinct from BackToBackLcPanel, which is scoped to a single master LC inside
 * ExportLcDetail.
 */
const BackToBackLcRegister: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();

    const [rows, setRows] = useState<BackToBackLc[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [status, setStatus] = useState<'ALL' | B2bLcStatus>('ALL');
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await BackToBackLcService.listByInstitution(institutionId, {
                page, size, status: status === 'ALL' ? undefined : status,
            });
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load back-to-back LC register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, status]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.b2bRegister.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Back-to-Back LC Register</Typography>

            <Box data-help-section="commercial.b2bRegister.filters" sx={{ mb: 2 }}>
                <TextField
                    data-help="commercial.b2bRegister.statusFilter" select label="Status" size="small" value={status}
                    onChange={(e) => { setStatus(e.target.value as 'ALL' | B2bLcStatus); setPage(0); }}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    <MenuItem value="ALL">All</MenuItem>
                    {B2B_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                </TextField>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.b2bRegister.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.b2bRegister.col.number">B2B LC Number</TableCell>
                                <TableCell data-help="commercial.b2bRegister.col.supplier">Supplier</TableCell>
                                <TableCell align="right" data-help="commercial.b2bRegister.col.amount">Amount</TableCell>
                                <TableCell align="right" data-help="commercial.b2bRegister.col.utilized">Utilized</TableCell>
                                <TableCell align="right" data-help="commercial.b2bRegister.col.headroom">Headroom</TableCell>
                                <TableCell data-help="commercial.b2bRegister.col.status">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(b => (
                                <TableRow key={b.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/back-to-back-lc/${b.masterLcId}/${b.id}`)}>
                                    <TableCell>{b.b2bLcNumber}</TableCell>
                                    <TableCell>{b.supplierName}</TableCell>
                                    <TableCell align="right">{formatMoney(b.amount, b.currencyCode)}</TableCell>
                                    <TableCell align="right">{formatMoney(b.utilizedAmount, b.currencyCode)}</TableCell>
                                    <TableCell align="right">{formatMoney(b.remainingHeadroom, b.currencyCode)}</TableCell>
                                    <TableCell><Chip data-help="commercial.b2bRegister.status" size="small" label={enumLabel(b.status)} color={B2B_STATUS_COLORS[b.status]} /></TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center">No back-to-back LCs found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default BackToBackLcRegister;
